/**
 * Edge Config Persistence
 *
 * Shared utility for loading/saving edge settings from localStorage.
 * Used by both EdgeIntegrationService and EdgeSettingsPanel.
 */

import {
  DEFAULT_EDGELESS_GRID_STYLE,
  EDGELESS_GRID_STYLE_ATTRIBUTE,
  EDGELESS_GRID_STYLE_CHANGED_EVENT,
  type EdgelessGridStyle,
  normalizeEdgelessGridStyle,
} from '@blocksuite/affine-block-surface';

import type { EdgePathType, EdgeRoutingType, EdgeSystemState } from '../types';
import { DEFAULT_EDGE_STATE } from '../types';

/**
 * LocalStorage key for edge settings
 */
export const EDGE_SETTINGS_STORAGE_KEY = 'streamlain-edge-settings';

/**
 * Persisted edge settings structure
 */
export interface PersistedEdgeSettings {
  state: EdgeSystemState;
  pathType: EdgePathType;
  routingType: EdgeRoutingType;
  gridStyle: EdgelessGridStyle;
}

/**
 * Default settings
 */
export const DEFAULT_EDGE_SETTINGS: PersistedEdgeSettings = {
  state: { ...DEFAULT_EDGE_STATE },
  pathType: 'bezier',
  routingType: 'direct',
  gridStyle: DEFAULT_EDGELESS_GRID_STYLE,
};

export function applyCanvasGridStyle(style: EdgelessGridStyle): void {
  if (typeof document === 'undefined') return;

  const normalized = normalizeEdgelessGridStyle(style);
  document.documentElement.setAttribute(
    EDGELESS_GRID_STYLE_ATTRIBUTE,
    normalized
  );
  window.dispatchEvent(new Event(EDGELESS_GRID_STYLE_CHANGED_EVENT));
}

/**
 * Load edge settings from localStorage
 */
export function loadEdgeSettings(): PersistedEdgeSettings {
  try {
    const stored = localStorage.getItem(EDGE_SETTINGS_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as Partial<PersistedEdgeSettings>;
      return {
        state: {
          automation: {
            ...DEFAULT_EDGE_STATE.automation,
            ...parsed.state?.automation,
            baseOpacity: 100,
          },
          relation: {
            ...DEFAULT_EDGE_STATE.relation,
            ...parsed.state?.relation,
            baseOpacity: 100,
          },
        },
        pathType: parsed.pathType ?? 'rounded-elbow',
        routingType: parsed.routingType ?? 'direct',
        gridStyle: normalizeEdgelessGridStyle(parsed.gridStyle),
      };
    }
  } catch {
    // Ignore errors, use defaults
  }
  return { ...DEFAULT_EDGE_SETTINGS };
}

/**
 * Save edge settings to localStorage
 */
export function saveEdgeSettings(settings: PersistedEdgeSettings): void {
  try {
    localStorage.setItem(EDGE_SETTINGS_STORAGE_KEY, JSON.stringify(settings));
  } catch {
    // Ignore errors
  }
  applyCanvasGridStyle(settings.gridStyle);
}
